import React, { useEffect, useState } from "react";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import { ScheduleList, TimetableList } from "../../api/TimeTableService";
import { formatTime, SweetAlertShow } from "../master/Master";
import { getBatchNCourseList } from "../../api/BatchService";
import { StaffList } from "../../api/Staff";
import Master from "./Master";
import { printWithProfile } from "../master/Print";
import usePrivateLink from "../config/usePrivateLink";

export default function Timetable() {
  const hasAccess = usePrivateLink();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [batches, setBatches] = useState([]);
  const [staffs, setStaffs] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [filterBatch, setFilterBatch] = useState("");
  const [filterTeacher, setFilterTeacher] = useState("");

  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  const getTodayDay = () => {
    const todayDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    return todayDays[new Date().getDay()];
  };

  const [Day, setDay] = useState(getTodayDay());

  const dayNames = {
    Mon: "Monday",
    Tue: "Tuesday",
    Wed: "Wednesday",
    Thu: "Thursday",
    Fri: "Friday",
    Sat: "Saturday",
    Sun: "Sunday",
  };

  const parseDays = (value) => {
    if (Array.isArray(value)) return value;

    try {
      return JSON.parse(value || "[]");
    } catch {
      return [];
    }
  };

  const getScheduleList = async () => {
    try {
      const res = await ScheduleList();

      if (res?.data?.status) {
        setSchedules(res?.data?.data || []);
      } else {
        setSchedules([]);
        SweetAlertShow(
          "info",
          "Warning",
          res?.data?.message || "Schedules not found.",
        );
      }
    } catch (error) {
      setSchedules([]);
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ||
          error?.message ||
          "Something went wrong.",
      );
    }
  };

  const batchList = async () => {
    try {
      const res = await getBatchNCourseList();

      if (res?.data?.status) {
        setBatches(res?.data?.data || []);
      } else {
        setBatches([]);
        SweetAlertShow(
          "info",
          "Warning",
          res?.data?.message || "Batches not found.",
        );
      }
    } catch (error) {
      setBatches([]);
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ||
          error?.message ||
          "Something went wrong.",
      );
    }
  };

  const getStaffList = async () => {
    try {
      const res = await StaffList();

      setStaffs(
        res?.data?.status && Array.isArray(res?.data?.data)
          ? res.data.data
          : [],
      );
    } catch {
      setStaffs([]);
    }
  };

  const getTimetableList = async () => {
    try {
      setLoading(true);

      let addUrl = `&day=${Day}`;

      if (filterBatch) addUrl += `&batch_id=${filterBatch}`;
      if (filterTeacher) addUrl += `&teacher_id=${filterTeacher}`;

      const res = await TimetableList(addUrl);

      setData(res?.data?.status ? res?.data?.data || [] : []);
    } catch (error) {
      setData([]);

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ||
          error?.message ||
          "Unable to load timetable.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getScheduleList();
    batchList();
    getStaffList();
  }, []);

  useEffect(() => {
    getTimetableList();
  }, [Day, filterBatch, filterTeacher]);

  const findGroup = (batchId, scheduleId, dayFilter) => {
    return (data || []).filter((item) => {
      const sameBatch = String(item.batch_id) === String(batchId);
      const sameSchedule = String(item.schedule_id) === String(scheduleId);
      const itemDays = parseDays(item.day);

      const sameDay =
        !dayFilter || dayFilter === "All" || itemDays.includes(dayFilter);

      return sameBatch && sameSchedule && sameDay;
    });
  };

  const resetFilters = () => {
    setFilterBatch("");
    setFilterTeacher("");
    setDay("Mon");
  };

  const getSubjectName = (item) =>
    item?.subject?.name ||
    item?.subject?.subject_name ||
    `Subject ${item.subject_id}`;

  const getTeacherName = (item) =>
    item?.teacher?.name || `Teacher ${item.teacher_id}`;

  const getScheduleTime = (schedule) =>
    `${formatTime(schedule.start_time)} - ${formatTime(schedule.end_time)}`;

  const assignedCount = (data || []).length;

  return (
    <div className="layout-wrapper layout-content-navbar timetable-page">
      <style>{`
        .timetable-page {
          --tt-primary: var(--bs-primary, #3659c9);
          --tt-border: var(--bs-border-color, #e4e8f0);
          --tt-muted: var(--bs-secondary-color, #778197);
        }

        .timetable-page .tt-panel {
          border: 1px solid var(--tt-border);
          border-radius: 14px;
          overflow: hidden;
          box-shadow: 0 3px 14px rgba(30, 50, 90, 0.045);
        }

        .timetable-page .tt-panel-header {
          padding: 20px 22px;
          border-bottom: 1px solid var(--tt-border);
        }

        .timetable-page .tt-heading {
          font-size: 1.1rem;
          font-weight: 750;
          letter-spacing: -0.35px;
          margin: 0;
        }

        .timetable-page .tt-subtitle {
          font-size: 13px;
          color: var(--tt-muted);
          margin-top: 5px;
          margin-bottom: 0;
        }

        .timetable-page .tt-day-list {
          display: grid;
          grid-template-columns: repeat(7, minmax(66px, 1fr));
          gap: 8px;
        }

        .timetable-page .tt-day-btn {
          min-height: 58px;
          border: 1px solid var(--tt-border);
          border-radius: 10px;
          background: var(--bs-tertiary-bg, #f8f9fc);
          color: var(--bs-body-color);
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          gap: 3px;
          transition: all 0.18s ease;
        }

        .timetable-page .tt-day-btn:hover {
          border-color: var(--tt-primary);
        }

        .timetable-page .tt-day-btn.active {
          background: var(--tt-primary);
          color: #fff;
          border-color: var(--tt-primary);
          box-shadow: 0 4px 10px rgba(54, 89, 201, 0.2);
        }

        .timetable-page .tt-day-short {
          font-size: 14px;
          font-weight: 750;
        }

        .timetable-page .tt-day-full {
          font-size: 10px;
          opacity: 0.8;
        }

        .timetable-page .tt-filter-label {
          display: block;
          font-size: 12px;
          font-weight: 700;
          margin-bottom: 8px;
          color: var(--bs-body-color);
        }

        .timetable-page .tt-filter {
          min-height: 43px;
          border-radius: 9px;
          font-size: 13px;
        }

        .timetable-page .tt-summary {
          display: flex;
          align-items: center;
          gap: 9px;
          flex-wrap: wrap;
        }

        .timetable-page .tt-summary-item {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          padding: 7px 11px;
          border: 1px solid var(--tt-border);
          border-radius: 8px;
          font-size: 12px;
          background: var(--bs-tertiary-bg, #f8f9fc);
        }

        .timetable-page .tt-table-wrap {
  width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  max-height: none;
}

        .timetable-page .tt-table {
  width: 100%;
  min-width: 950px;
  border-collapse: separate;
  border-spacing: 0;
  margin: 0;
}

        .timetable-page .tt-table th,
.timetable-page .tt-table td {
  padding: 8px;
  vertical-align: top;
}

        .timetable-page .tt-table thead th {
          position: sticky;
          top: 0;
          z-index: 3;
          background: var(--bs-tertiary-bg, #f5f7fc);
          text-align: center;
          vertical-align: middle;
          min-width: 190px;
        }

        .timetable-page .tt-table .tt-batch-heading {
          position: sticky;
          left: 0;
          z-index: 4;
          min-width: 190px;
          width: 190px;
          text-align: left;
          background: var(--bs-tertiary-bg, #f5f7fc);
        }

        .timetable-page .tt-table tbody .tt-batch-cell {
          position: sticky;
          left: 0;
          z-index: 2;
          min-width: 190px;
          background: var(--bs-body-bg);
        }

        .timetable-page .tt-period-number {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 27px;
          height: 27px;
          border-radius: 8px;
          background: rgba(54, 89, 201, 0.1);
          color: var(--tt-primary);
          font-size: 12px;
          font-weight: 800;
          margin-bottom: 7px;
        }

        .timetable-page .tt-period-name {
          font-weight: 750;
          font-size: 13px;
          margin-bottom: 5px;
          white-space: normal;
        }

        .timetable-page .tt-period-time {
          font-size: 11px;
          font-weight: 500;
          color: var(--tt-muted);
          white-space: nowrap;
        }

        .timetable-page .tt-batch-name {
          font-size: 13px;
          font-weight: 750;
          line-height: 1.45;
          overflow-wrap: anywhere;
        }

        .timetable-page .tt-batch-label {
          font-size: 10px;
          color: var(--tt-muted);
          margin-top: 5px;
        }

        .timetable-page .tt-class-card {
  padding: 9px;
  border: 1px solid rgba(54, 89, 201, 0.16);
  border-left: 3px solid var(--tt-primary);
  border-radius: 8px;
  background: var(--bs-tertiary-bg, #f6f8ff);
  text-align: left;
  overflow-wrap: anywhere;
  height: auto;
  min-height: 0;
}

        .timetable-page .tt-subject {
  font-size: 13px;
  font-weight: 750;
  line-height: 1.4;
  margin-bottom: 5px;
  color: var(--bs-body-color);
  overflow-wrap: anywhere;
}

        .timetable-page .tt-teacher {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 11px;
  line-height: 1.4;
  color: var(--tt-muted);
  overflow-wrap: anywhere;
}

        .timetable-page .tt-teacher i {
          color: var(--tt-primary);
          font-size: 13px;
          margin-top: 1px;
        }

        .timetable-page .tt-free {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px 4px;
  height: auto;
  min-height: 0;
  color: var(--tt-muted);
  font-size: 11px;
  border: 1px dashed var(--tt-border);
  border-radius: 8px;
  background: var(--bs-tertiary-bg, #fafbfc);
}

        .timetable-page .tt-free i {
          font-size: 17px;
          opacity: 0.65;
        }

        .timetable-page .tt-entry-separator {
  border-top: 1px dashed var(--tt-border);
  margin-top: 7px;
  padding-top: 7px;
}

        .timetable-page .tt-empty {
          padding: 55px 20px;
          text-align: center;
        }

        .timetable-page .tt-empty-icon {
          display: inline-flex;
          width: 58px;
          height: 58px;
          align-items: center;
          justify-content: center;
          border-radius: 16px;
          background: var(--bs-tertiary-bg, #f1f4fa);
          font-size: 26px;
          margin-bottom: 14px;
          color: var(--tt-primary);
        }

        .timetable-page .tt-empty-title {
          font-weight: 750;
          font-size: 15px;
          margin-bottom: 6px;
        }

        .timetable-page .tt-empty-text {
          font-size: 13px;
          color: var(--tt-muted);
          margin: 0;
        }

        @media (max-width: 767.98px) {
          .timetable-page .tt-panel-header {
            padding: 16px;
          }

          .timetable-page .tt-day-list {
            grid-template-columns: repeat(4, minmax(0, 1fr));
          }

          .timetable-page .tt-day-btn {
            min-height: 52px;
          }

          .timetable-page .tt-summary {
            margin-top: 12px;
          }

          .timetable-page .tt-table-wrap {
            max-height: none;
          }
        }

        @media print {
          .timetable-page .tt-table-wrap {
            max-height: none !important;
            overflow: visible !important;
          }

          .timetable-page .tt-table {
            min-width: 0 !important;
            width: 100% !important;
            font-size: 10px;
          }

          .timetable-page .tt-table thead th,
          .timetable-page .tt-table .tt-batch-heading,
          .timetable-page .tt-table tbody .tt-batch-cell {
            position: static !important;
          }

          .timetable-page .tt-table th,
          .timetable-page .tt-table td {
            padding: 6px;
          }

          .timetable-page .tt-class-card,
          .timetable-page .tt-free {
            min-height: 0;
            padding: 6px;
            break-inside: avoid;
          }

          .timetable-page .tt-panel {
            box-shadow: none;
          }
        }
      `}</style>

      <div className="layout-container">
        <Header />

        <div className="layout-page">
          <Nav />

          <div className="content-wrapper">
            <div className="container-fluid mt-3">
              <div className="row">
                <Master />

                <div className="col-md-12">
                  {/* Page heading */}
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
                    <div>
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <div
                          className="d-flex align-items-center justify-content-center rounded-3"
                          style={{
                            width: 44,
                            height: 44,
                            background: "rgba(54, 89, 201, 0.10)",
                            color: "var(--bs-primary)",
                            fontSize: 22,
                          }}
                        >
                          <i className="ri-calendar-schedule-line"></i>
                        </div>

                        <div>
                          <h4 className="mb-0 fw-bold">School Timetable</h4>
                          <div className="text-muted small mt-1">
                            Manage and view your weekly class schedule
                          </div>
                        </div>
                      </div>
                    </div>

                    {hasAccess("/timetable/print") && (
                      <button
                        type="button"
                        className="btn btn-outline-primary d-flex align-items-center gap-2"
                        onClick={() =>
                          printWithProfile("PrintDiv", {
                            profile: "timetable",
                            title: `${dayNames[Day] || Day} Timetable`,
                            pageSize: "A4",
                            orientation: "landscape",
                            margin: "7mm",
                          })
                        }
                      >
                        <i className="ri-printer-line"></i>
                        Print Timetable
                      </button>
                    )}
                  </div>

                  {/* Filters and day selection */}
                  <div className="card tt-panel mb-4">
                    <div className="tt-panel-header">
                      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                        <div>
                          <h6 className="tt-heading">
                            <i className="ri-calendar-check-line me-2 text-primary"></i>
                            Select Schedule Day
                          </h6>
                          <p className="tt-subtitle">
                            Choose a day to view the corresponding periods
                          </p>
                        </div>
                      </div>

                      <div className="tt-day-list">
                        {days.map((day) => (
                          <button
                            type="button"
                            key={day}
                            className={`tt-day-btn ${
                              day === Day ? "active" : ""
                            }`}
                            onClick={() => setDay(day)}
                            aria-pressed={day === Day}
                          >
                            <span className="tt-day-short">{day}</span>
                            <span className="tt-day-full">{dayNames[day]}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="card-body p-3 p-md-4">
                      <div className="d-flex align-items-center gap-2 mb-3">
                        <i className="ri-filter-3-line text-primary fs-5"></i>
                        <h6 className="mb-0 fw-bold">Filter Timetable</h6>
                      </div>

                      <div className="row g-3 align-items-end">
                        <div className="col-xl-4 col-md-5 col-sm-6">
                          <label className="tt-filter-label">
                            <i className="ri-building-2-line me-1"></i>
                            Batch / Class
                          </label>

                          <select
                            className="form-select tt-filter"
                            value={filterBatch}
                            onChange={(e) => setFilterBatch(e.target.value)}
                          >
                            <option value="">All Batches</option>
                            {batches.map((batch) => (
                              <option key={batch.id} value={batch.id}>
                                {batch.title}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="col-xl-4 col-md-5 col-sm-6">
                          <label className="tt-filter-label">
                            <i className="ri-user-star-line me-1"></i>
                            Teacher
                          </label>

                          <select
                            className="form-select tt-filter"
                            value={filterTeacher}
                            onChange={(e) => setFilterTeacher(e.target.value)}
                          >
                            <option value="">All Teachers</option>
                            {staffs.map((staff) => (
                              <option key={staff.id} value={staff.id}>
                                {staff.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="col-xl-4 col-md-2 col-12">
                          <button
                            type="button"
                            className="btn btn-outline-secondary tt-filter w-100 d-flex align-items-center justify-content-center gap-2"
                            onClick={resetFilters}
                          >
                            <i className="ri-refresh-line"></i>
                            Reset Filters
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Timetable grid */}
                  <div className="card tt-panel mb-4" id="PrintDiv">
                    <div className="tt-panel-header">
                      <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
                        <div>
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <i className="ri-calendar-event-line text-primary fs-4"></i>
                            <h5 className="tt-heading">
                              {dayNames[Day] || Day} Timetable
                            </h5>
                          </div>

                          <p className="tt-subtitle">
                            Period-wise subject and teacher allocation
                          </p>
                        </div>

                        <div className="tt-summary">
                          <div className="tt-summary-item">
                            <i className="ri-time-line text-primary"></i>
                            <span>
                              <strong>{schedules.length}</strong> Periods
                            </span>
                          </div>

                          <div className="tt-summary-item">
                            <i className="ri-book-open-line text-primary"></i>
                            <span>
                              <strong>{assignedCount}</strong> Assignments
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="card-body p-0">
                      {loading ? (
                        <div className="tt-empty">
                          <div
                            className="spinner-border text-primary mb-3"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>

                          <div className="tt-empty-title">
                            Loading timetable
                          </div>

                          <p className="tt-empty-text">
                            Fetching the class schedule for{" "}
                            {dayNames[Day] || Day}.
                          </p>
                        </div>
                      ) : schedules.length === 0 ? (
                        <div className="tt-empty">
                          <div className="tt-empty-icon">
                            <i className="ri-calendar-close-line"></i>
                          </div>

                          <div className="tt-empty-title">
                            No periods configured
                          </div>

                          <p className="tt-empty-text">
                            No schedule periods are available to display.
                          </p>
                        </div>
                      ) : batches.length === 0 ? (
                        <div className="tt-empty">
                          <div className="tt-empty-icon">
                            <i className="ri-building-2-line"></i>
                          </div>

                          <div className="tt-empty-title">No batches found</div>

                          <p className="tt-empty-text">
                            There are no batches available for the timetable.
                          </p>
                        </div>
                      ) : (
                        <div className="tt-table-wrap">
                          <table className="table tt-table">
                            <thead>
                              <tr>
                                <th className="tt-batch-heading">
                                  <div className="d-flex align-items-center gap-2">
                                    <i className="ri-building-2-line text-primary fs-5"></i>
                                    <div>
                                      <div className="fw-bold">
                                        Class / Batch
                                      </div>
                                      <div className="tt-period-time">
                                        {batches.length} batches
                                      </div>
                                    </div>
                                  </div>
                                </th>

                                {schedules.map((schedule, index) => (
                                  <th key={schedule.id}>
                                    <div className="tt-period-number">
                                      {index + 1}
                                    </div>

                                    <div className="tt-period-name">
                                      {schedule.title}
                                    </div>

                                    <div className="tt-period-time">
                                      <i className="ri-time-line me-1"></i>
                                      {getScheduleTime(schedule)}
                                    </div>
                                  </th>
                                ))}
                              </tr>
                            </thead>

                            <tbody>
                              {batches.map((batch) => (
                                <tr key={batch.id}>
                                  <th scope="row" className="tt-batch-cell">
                                    <div className="d-flex align-items-start gap-2">
                                      <div
                                        className="d-flex align-items-center justify-content-center rounded-2 flex-shrink-0"
                                        style={{
                                          width: 34,
                                          height: 34,
                                          background: "rgba(54, 89, 201, 0.10)",
                                          color: "var(--bs-primary)",
                                        }}
                                      >
                                        <i className="ri-graduation-cap-line fs-5"></i>
                                      </div>

                                      <div>
                                        <div className="tt-batch-name">
                                          {batch.title}
                                        </div>
                                        <div className="tt-batch-label">
                                          Class schedule
                                        </div>
                                      </div>
                                    </div>
                                  </th>

                                  {schedules.map((schedule) => {
                                    const group = findGroup(
                                      batch.id,
                                      schedule.id,
                                      Day,
                                    );

                                    return (
                                      <td key={schedule.id}>
                                        {group.length > 0 ? (
                                          group.map((item, index) => (
                                            <div
                                              key={item.id}
                                              className={
                                                index > 0
                                                  ? "tt-entry-separator"
                                                  : ""
                                              }
                                            >
                                              <div className="tt-class-card">
                                                <div className="tt-subject">
                                                  <i className="ri-book-2-line text-primary me-1"></i>
                                                  {getSubjectName(item)}
                                                </div>

                                                <div className="tt-teacher">
                                                  <i className="ri-user-line"></i>
                                                  <span>
                                                    {getTeacherName(item)}
                                                  </span>
                                                </div>
                                              </div>
                                            </div>
                                          ))
                                        ) : (
                                          <div className="tt-free">
                                            <i className="ri-calendar-check-line"></i>
                                            <span>Free period</span>
                                          </div>
                                        )}
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>

                    {!loading && schedules.length > 0 && batches.length > 0 && (
                      <div className="card-body border-top py-3 px-3 px-md-4">
                        <div className="d-flex align-items-center flex-wrap gap-3">
                          <span className="small fw-semibold text-muted">
                            LEGEND
                          </span>

                          <span className="d-inline-flex align-items-center gap-2 small">
                            <span
                              style={{
                                width: 12,
                                height: 12,
                                borderRadius: 3,
                                background: "var(--bs-primary)",
                                display: "inline-block",
                              }}
                            ></span>
                            Assigned subject
                          </span>

                          <span className="d-inline-flex align-items-center gap-2 small text-muted">
                            <i className="ri-calendar-check-line"></i>
                            Free period
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="text-muted small mb-4">
                    <i className="ri-information-line me-1"></i>
                    Showing the {dayNames[Day] || Day} schedule. Use the filters
                    above to narrow the timetable by batch or teacher.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
